import * as comlink from "comlink"

import type { UploadFinishedStatusDatagram } from "~/models/polycache/UploadFinishedStatusDatagram"

import { StaticFile } from "../file/StaticFile"
// TODO: maybe want a web and a native version of this class?
export type UploadJobStatus = "counting" | "preparing" | "uploading" | "failed" | "complete"
export type UploadJobFileStatus = "counted" | "prepared" | "uploading" | "uploaded" | "complete" | "failed"

export interface UploadJobState {
  type: "upload"
  transferId: string
  status: UploadJobStatus
  title?: string // the folder name to upload, or something else, etc.
  destination?: StaticFile
  timestamp: number
  folderId: string
  files: Record<string, {
    fileId: string
    preHash: string
    name: string
    totalBytes: number
    completedBytes: number
    parentId: string
    isFolder: boolean
    status: UploadJobFileStatus
    browserFile?: globalThis.File
  }>
}

/**
 * The root upload job class, which defines any job, whether it is on this tab/window,
 * or an external source of truth. Subclasses of this class include the BrowserUploadJob,
 * which is a browser-based upload job, as well as a DesktopUploadJob, which is an upload
 * job started on desktop. The root job class is created when we receive a notification
 * of another tab's job.
 */
export class UploadJob {
  state: Ref<UploadJobState>

  /**
   * A list of files that we, for sure, do not want to include in uploads. This currently
   * includes the mac finder DS_Store file. In the future this may include other kinds of
   * irrelevant files.
   */
  protected bannedFiles = new Set([".DS_Store"])

  constructor(state: Ref<UploadJobState>) {
    this.state = state
  }

  /**
   * This has to update our core jobs dict instead of our own
   * local state
   */
  started(start: UploadStartedStatusDatagram) {
    const { transferId, folderId } = start
    if (transferId !== this.state.value.transferId || folderId !== this.state.value.folderId)
      return console.error("This update doesn't match", start)
    this.state.value.status = "counting"
  }

  fileUpdated(update: UploadFileProgressStatusDatagram) {
    const { transferId, fileId, totalBytes, completedBytes } = update
    if (transferId !== this.state.value.transferId)
      return console.error("This update doesn't match", update)
    if (!isDefined(this.state.value.files[fileId]))
      return console.error("This update references an unknown file", update)

    this.state.value.status = "uploading"
    this.state.value.files[fileId].completedBytes = Number(completedBytes)
    this.state.value.files[fileId].totalBytes = Number(totalBytes)
    if (this.state.value.files[fileId].totalBytes === this.state.value.files[fileId].completedBytes)
      this.state.value.files[fileId].status = "uploaded"
    else
      this.state.value.files[fileId].status = "uploading"
    // TODO: do we do anything with this?
  }

  fileFailed(update: UploadFileFailedStatusDatagram) {
    if (update.transferId !== this.state.value.transferId)
      return console.error("This update doesn't match", update)
    if (!isDefined(this.state.value.files[update.file.id]))
      return console.error("This update references an unknown file", update)
    this.state.value.files[update.file.id].completedBytes = this.state.value.files[update.file.id].totalBytes
    this.state.value.files[update.file.id].status = "failed"
  }

  finished(finished: UploadFinishedStatusDatagram) {
    if (finished.transferId !== this.state.value.transferId || finished.folderId !== this.state.value.folderId)
      return console.error("This update doesn't match", finished)

    if (!finished.totalBytes || finished.totalBytes !== finished.failedBytes)
      this.state.value.status = "complete"
    else
      this.state.value.status = "failed"
  }

  #bytesByType = (type: UploadJobFileStatus) => computed(() => Object
    .values(this.state.value.files)
    .filter(f => f.status === type)
    .map(f => f.totalBytes)
    .reduce((a, b) => a + b, 0))

  totalFiles = computed(() => Object.values(this.state.value.files).length)
  totalBytes = computed(() => Object.values(this.state.value.files).map(f => f.totalBytes).reduce((a, b) => a + b, 0))
  /** This includes all uploaded, completed, or failed bytes */
  finishedBytes = computed(() => Object.values(this.state.value.files).map(f => f.completedBytes).reduce((a, b) => a + b, 0))

  countedBytes = this.#bytesByType("counted")
  preparedBytes = this.#bytesByType("prepared")
  uploadingBytes = this.#bytesByType("uploading")
  uploadedBytes = this.#bytesByType("uploaded")
  completedBytes = this.#bytesByType("complete")
  failedBytes = this.#bytesByType("failed")

  progressDetails = () => {
    return {
      finishedBytes: this.finishedBytes.value,
      successBytes: this.completedBytes.value,
      failedBytes: this.failedBytes.value,
      totalBytes: this.totalBytes.value,
    }
  }
}

/**
 * A browser-based upload job that can parse the contents of a `DataTransfer` object and
 * construct a job based out of that.
 */
export class BrowserUploadJob extends UploadJob {
  async fetchAndUpload(destination: StaticFile, link: URL) {
    const { notifyError } = useAlerts()
    const { cacheWorker } = useCache()
    const { sandboxWorker } = useSandbox()
    const { state: myself } = useSelf()
    const root = destination.rootFolder()?.id
    const archive = myself.value.archive
    if (!root || !archive || root === archive.id) {
      notifyError(
        "Cannot upload into archive",
        "The archive directory is for discarded files. Please choose a different destination.",
      )
      throw new Error("Cannot upload into your archive directory")
    }
    if (!myself.value.user)
      return notifyError("Cannot upload while logged out", "Please log in to upload files.")

    const userId = myself.value.user.id
    const preHash = StaticFile.clientPreHashId()
    const fileId = await StaticFile.idFromPrehash(preHash)
    const parentId = destination.id

    // download the link into a browserFile
    const response = await fetch(link)
    const blob = await response.blob()
    const filename = link.pathname.split("/").pop() || "downloaded_file"
    const browserFile = new File([blob], filename, { type: blob.type })

    this.state.value.files[fileId] = {
      fileId,
      preHash,
      isFolder: false,
      name: filename,
      browserFile,
      totalBytes: browserFile.size,
      completedBytes: 0,
      parentId,
      status: "counted",
    }

    const request = sandboxWorker().parseFileIntoFileCreateRequest(userId, preHash, parentId, browserFile, link.href)
    const buffer: ArrayBuffer = await withTimeout(request, 10 * 60 * 1000) // 10 minutes

    this.state.value.files[fileId].status = "prepared"
    this.state.value.status = "preparing"

    cacheWorker().uploadItems(
      this.state.value.transferId,
      this.state.value.folderId,
      userId,
      comlink.transfer([buffer], [buffer]), // makes the arraybuffers zero-copy
    )
  }

  async startUpload(destination: StaticFile, transfer: DataTransfer) {
    const { notifyError } = useAlerts()
    const { cacheWorker } = useCache()
    const { state: myself } = useSelf()
    const root = destination.rootFolder()?.id
    const archive = myself.value.archive
    if (!root || !archive || root === archive.id) {
      notifyError(
        "Cannot upload into archive",
        "The archive directory is for discarded files. Please choose a different destination.",
      )
      throw new Error("Cannot upload into your archive directory")
    }
    if (!myself.value.user)
      return notifyError("Cannot upload while logged out", "Please log in to upload files.")

    const userId = myself.value.user.id
    const requests = await this.parseFiles(transfer)
    cacheWorker().uploadItems(
      this.state.value.transferId,
      this.state.value.folderId,
      userId,
      comlink.transfer(requests, requests), // makes the arraybuffers zero-copy
    )
  }

  /**
   * Takes all the data in the data transfer and creates interface file objects with
   * the metadata filled in. Runs a bunch of parallel copy tasks to place the uploads
   * within our sandbox. It returns an array of ArrayBuffers, each of which represent
   * a `FileCreateRequestDatagram`. We can send this datagram to the cache, which can
   * then do a large upload task with it.
   */
  private async parseFiles(transfer: DataTransfer, sandboxConcurrency = 50) {
    const { state: myself } = useSelf()
    const { sandboxWorker } = useSandbox()
    if (!myself.value.user)
      throw new Error("Not logged in, uploading not implemented for anon")

    const parentId = this.state.value.folderId
    const userId = myself.value.user!.id

    // We have to call `webkitGetAsEntry` on each item in the transfer before we hit
    // any await points, otherwise the entries will be null.
    const items = [...zip([...transfer.items as any], [...transfer.files as any])]
    const entries = items.map(([item, fileItem]) => [item, fileItem, item.webkitGetAsEntry()])

    // first, lets recursively parse all the folders and files, and place them into our
    // 'files' dict. Note that on modern browsers, the `webkitGetAsEntry` method
    // is available, so the fallback is only for old browsers, and that fallback unfortunately
    // does not support folders.
    for (const [item, fileItem, entry] of entries) {
      if (entry)
        await this._addUploadEntriesRecursively(entry, userId, parentId)
      else if (item.getAsFile() || fileItem) {
        const file = item.getAsFile() || fileItem
        if (this.bannedFiles.has(file.name)) {
          // add a dummy file id to the state
          const fileId = StaticFile.clientPreHashId()
          this.state.value.files[fileId] = {
            fileId,
            preHash: fileId,
            name: file.name,
            isFolder: false,
            totalBytes: file.size,
            completedBytes: 0,
            parentId,
            status: "failed",
          }
          continue
        }
        else {
          const preHash = StaticFile.clientPreHashId()
          const fileId = await StaticFile.idFromPrehash(preHash)
          this.state.value.files[fileId] = {
            fileId,
            preHash,
            isFolder: false,
            name: file.name,
            browserFile: file,
            totalBytes: file.size,
            completedBytes: 0,
            parentId,
            status: "counted",
          }
        }
      }
      else
        console.error("Unknown data transfer, skipping", item)
    }

    this.state.value.status = "preparing"

    // now we iterate over the files, "mapping" them into array buffers that represent file create requests
    const buffers = mapPool(this.files(), async ({ fileId, isFolder, preHash, browserFile, name, parentId, status }) => {
      let request
      if (status === "failed")
        return
      else if (!isFolder && browserFile)
        request = sandboxWorker().parseFileIntoFileCreateRequest(userId, preHash, parentId, browserFile)
      else if (isFolder)
        request = sandboxWorker().parseFolderIntoFileCreateRequest(userId, preHash, parentId, name)
      else {
        console.error("Invalid file parse output", fileId, isFolder, browserFile, name, parentId, status)
        throw new Error("Invalid file")
      }
      try {
        // NOTE: Very long files will take forever to even allocate, so this timeout might
        // fail for perfectly valid files, above ~3gb
        const buffer: ArrayBuffer = await withTimeout(request, 10 * 60 * 1000) // 10 minutes
        this.state.value.files[fileId].status = "prepared"
        return buffer
      }
      catch (error) {
        console.error("Failed to prepare file", error, fileId, isFolder, preHash, browserFile, name, parentId, status)
        this.state.value.files[fileId].status = "failed"
      }
    }, sandboxConcurrency)

    const createRequests: ArrayBuffer[] = []
    for await (const value of buffers)
      if (isDefined(value))
        createRequests.push(value)

    console.debug("Prepared all files")

    return createRequests
  }

  async* files() {
    for (const file of Object.values(this.state.value.files))
      yield Promise.resolve(file)
  }

  private async _addUploadEntriesRecursively(
    entry: FileSystemEntry,
    userId: string,
    parentId: string,
    maxDepth = 16,
    maxFiles = 2 ** 16,
  ) {
    const { notifyError } = useAlerts()
    const preHash = StaticFile.clientPreHashId()
    const fileId = await StaticFile.idFromPrehash(preHash)
    if (maxDepth < 0)
      return notifyError("Can't upload this folder", `Folder exceeds max depth of ${maxDepth}`)
    if (entry.isFile) {
      const fileEntry = entry as FileSystemFileEntry
      const file = await new Promise<File>((s, e) => (fileEntry.file(s, e)))
      if (this.bannedFiles.has(file.name)) {
        this.state.value.files[fileId] = {
          fileId,
          preHash,
          name: file.name,
          isFolder: false,
          totalBytes: file.size,
          completedBytes: 0,
          parentId,
          status: "failed",
        }
      }
      else {
        this.state.value.files[fileId] = {
          fileId,
          preHash,
          name: file.name,
          isFolder: false,
          browserFile: file,
          totalBytes: file.size,
          completedBytes: 0,
          parentId,
          status: "counted",
        }
      }
      return
    }

    const dir = entry as FileSystemDirectoryEntry
    this.state.value.files[fileId] = {
      fileId,
      preHash,
      name: dir.name,
      isFolder: true,
      totalBytes: 0,
      completedBytes: 0,
      parentId,
      status: "counted",
    }
    for await (const item of this._listDirectoryEntry(dir)) {
      await this._addUploadEntriesRecursively(
        item,
        userId,
        fileId,
        maxDepth - 1,
        maxFiles,
      )
    }
  }

  /**
   * This method enumerates files in a folder that is uploaded. It's written like this
   * because old browsers only read 100 items at a time for some reason...
   */
  private async* _listDirectoryEntry(directory: FileSystemDirectoryEntry) {
    const directoryReader = (directory as FileSystemDirectoryEntry).createReader()
    while (true) {
      const entries = await new Promise<FileSystemEntry[]>(
        (s, e) => directoryReader.readEntries(s, e),
      )
      if (!entries.length)
        break
      for (const entry of entries)
        yield entry
    }
  }
}
